import React from 'react';
import { Calendar } from 'lucide-react';
import { LifeStats } from '../../hooks/useLifeStats';
import Card from '../Card';
import ProgressBar from '../ProgressBar';

interface CalendarViewProps {
  stats: LifeStats;
}

const CalendarView: React.FC<CalendarViewProps> = ({ stats }) => {
  if (stats.lifeExpectancy <= 0) return null;

  // Calculate total years and months
  const totalYears = Math.ceil(stats.lifeExpectancy);
  const totalMonths = totalYears * 12;
  
  // Month abbreviations for the header
  const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Generate GitHub-style heatmap
  const generateHeatmap = () => {
    const years = [];

    // Create header row with month names
    const headerRow = (
      <div className="flex mb-3 pl-14" key="month-header">
        {monthAbbreviations.map((month) => (
          <div
            key={`header-${month}`}
            className="w-16 text-center text-sm text-gray-500 dark:text-catppuccin-subtext0 font-medium"
          >
            {month}
          </div>
        ))}
      </div>
    );

    years.push(headerRow);

    // Generate rows for each year
    for (let year = 0; year < totalYears; year++) {
      const monthCells = [];

      // Add year label
      const yearLabel = (
        <div className="w-12 text-right pr-3 text-sm text-gray-500 dark:text-catppuccin-subtext0 font-medium flex items-center justify-end">
          {year + 1}
        </div>
      );

      // Add month cells for this year
      for (let month = 0; month < 12; month++) {
        const absoluteMonthIndex = year * 12 + month;
        const isPast = absoluteMonthIndex < stats.monthsLived;
        const isCurrent = absoluteMonthIndex === Math.floor(stats.monthsLived);

        // Different color shades based on progress through life
        let bgColor = "bg-gray-100 dark:bg-catppuccin-surface1"; // Future month
        let textColor = "text-gray-700 dark:text-catppuccin-subtext0";

        if (isPast) {
          const progress = absoluteMonthIndex / totalMonths;
          if (progress < 0.25) bgColor = "bg-green-200 dark:bg-green-800";
          else if (progress < 0.5) bgColor = "bg-yellow-200 dark:bg-yellow-800";
          else if (progress < 0.75) bgColor = "bg-orange-200 dark:bg-orange-800";
          else bgColor = "bg-red-200 dark:bg-red-800";
          textColor = "text-gray-800 dark:text-catppuccin-text";
        }

        if (isCurrent) {
          bgColor = "bg-blue-500 dark:bg-catppuccin-blue";
          textColor = "text-white";
        }

        monthCells.push(
          <div
            key={`month-${year}-${month}`}
            className={`w-16 h-12 ${bgColor} ${textColor} m-0.5 rounded-lg flex items-center justify-center text-sm font-medium transition-all duration-200 hover:brightness-110 hover:shadow-md cursor-pointer ${isCurrent ? 'ring-2 ring-blue-400 dark:ring-catppuccin-blue' : ''}`}
            title={`${monthAbbreviations[month]}, Year ${year + 1} (Month ${absoluteMonthIndex + 1} of life)`}
          >
            {month + 1}
          </div>
        );
      }

      // Add this year's row
      years.push(
        <div key={`year-${year}`} className="flex items-center mb-1">
          {yearLabel}
          <div className="flex">{monthCells}</div>
        </div>
      );
    }

    return years;
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <Card className="mb-6 p-6" animated={true}>
        <div className="flex items-center gap-3 mb-4">
          <Calendar size={24} className="text-blue-500 dark:text-catppuccin-blue" />
          <h2 className="text-xl font-bold dark:text-catppuccin-text">Months of Life</h2>
        </div>

        <div className="mb-6">
          <ProgressBar
            value={stats.monthsLived}
            maxValue={totalMonths}
            label={`${Math.floor(stats.monthsLived)} of ${totalMonths} months`}
            height="h-4"
            animate={true}
          />
        </div>

        <div className="max-h-[600px] overflow-y-auto overflow-x-auto pr-2 custom-scrollbar flex justify-center">
          <div>
            {generateHeatmap()}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CalendarView; 